import { useEffect, useState } from 'react';
import { Gauge, Loader2, Save, Trash2, Plus } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import { useUIStore } from '@/stores/ui-store';

/**
 * Upstream spend caps: what Rumio itself spends on platform keys serving one
 * site, in USD per day and per month. Over a cap, paid platform providers
 * pause for that site and free ones keep serving. Distinct from AI credits
 * (what the customer spends with us). The '*' row is the fleet default for
 * every site without its own row.
 */

interface SpendCapRow {
  tenant_site_id: string;
  monthly_cap_usd: number | null;
  daily_cap_usd: number | null;
  note: string | null;
}

interface SiteOption {
  site_id: string;
  workspace_name?: string | null;
}

interface Draft {
  daily: string;
  monthly: string;
  note: string;
}

const toDraft = (r: SpendCapRow): Draft => ({
  daily: r.daily_cap_usd == null ? '' : String(r.daily_cap_usd),
  monthly: r.monthly_cap_usd == null ? '' : String(r.monthly_cap_usd),
  note: r.note ?? '',
});

/** '' means no ceiling on that axis. */
function parseCap(v: string): number | null | 'invalid' {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : 'invalid';
}

export default function PlatformSpendCapsSection() {
  const addToast = useUIStore((s) => s.addToast);
  const [caps, setCaps] = useState<SpendCapRow[]>([]);
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [newSite, setNewSite] = useState('');

  function applyCaps(rows: SpendCapRow[]) {
    setCaps(rows);
    setDrafts(Object.fromEntries(rows.map((r) => [r.tenant_site_id, toDraft(r)])));
  }

  useEffect(() => {
    api
      .get<{ caps: SpendCapRow[]; sites: SiteOption[] }>('/admin/platform/ai/spend-caps')
      .then((res) => {
        applyCaps(res.caps);
        setSites(res.sites ?? []);
      })
      .catch(() => addToast('Failed to load spend caps', 'error'))
      .finally(() => setLoading(false));
  }, []);

  async function save(siteId: string) {
    const d = drafts[siteId];
    const daily = parseCap(d.daily);
    const monthly = parseCap(d.monthly);
    if (daily === 'invalid' || monthly === 'invalid') {
      addToast('Caps must be a dollar amount, or empty for no cap', 'error');
      return;
    }
    setSaving(siteId);
    try {
      const res = await api.put<{ caps: SpendCapRow[] }>(`/admin/platform/ai/spend-caps/${encodeURIComponent(siteId)}`, {
        daily_cap_usd: daily,
        monthly_cap_usd: monthly,
        note: d.note.trim() || null,
      });
      applyCaps(res.caps);
      addToast(siteId === '*' ? 'Fleet default saved' : `Caps saved for ${siteId}`, 'success');
    } catch (err: any) {
      addToast(err.message || 'Failed to save the caps', 'error');
    } finally {
      setSaving(null);
    }
  }

  async function remove(siteId: string) {
    if (!window.confirm(`Remove ${siteId}'s own caps? It will follow the fleet default.`)) return;
    setSaving(siteId);
    try {
      const res = await api.delete<{ caps: SpendCapRow[] }>(`/admin/platform/ai/spend-caps/${encodeURIComponent(siteId)}`);
      applyCaps(res.caps);
      addToast(`${siteId} now follows the fleet default`, 'success');
    } catch (err: any) {
      addToast(err.message || 'Failed to remove the caps', 'error');
    } finally {
      setSaving(null);
    }
  }

  function addSite() {
    const id = newSite.trim();
    if (!id || drafts[id]) return;
    const fleet = caps.find((c) => c.tenant_site_id === '*');
    setDrafts((cur) => ({ ...cur, [id]: fleet ? { ...toDraft(fleet), note: '' } : { daily: '', monthly: '', note: '' } }));
    setCaps((cur) => [...cur, { tenant_site_id: id, daily_cap_usd: null, monthly_cap_usd: null, note: null }]);
    setNewSite('');
  }

  const capped = new Set(caps.map((c) => c.tenant_site_id));

  return (
    <Card padding="lg" className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <Gauge className="w-5 h-5 text-purple-500" />
        <h3 className="text-lg font-semibold text-gray-900">AI spend caps</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        What Rumio spends upstream on platform AI keys for one site, in US dollars. When a site passes its daily or
        monthly cap, paid platform providers pause for that site until the period resets (midnight UTC / the 1st) and
        free providers keep serving. Leave a field empty for no cap. This is separate from the AI credits a site buys.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-2">
          <div className="hidden sm:grid grid-cols-[minmax(0,1.4fr)_6rem_6rem_minmax(0,1.6fr)_auto] gap-2 text-xs font-medium text-gray-500 px-1">
            <span>Site</span>
            <span>Per day ($)</span>
            <span>Per month ($)</span>
            <span>Note</span>
            <span />
          </div>
          {caps.map((row) => {
            const id = row.tenant_site_id;
            const d = drafts[id] ?? toDraft(row);
            const set = (patch: Partial<Draft>) => setDrafts((cur) => ({ ...cur, [id]: { ...d, ...patch } }));
            return (
              <div
                key={id}
                className="grid grid-cols-2 sm:grid-cols-[minmax(0,1.4fr)_6rem_6rem_minmax(0,1.6fr)_auto] gap-2 items-center rounded-md border border-gray-200 p-2"
                data-testid={`spend-cap-${id}`}
              >
                <span className="col-span-2 sm:col-span-1 text-sm font-medium text-gray-900 truncate">
                  {id === '*' ? 'Fleet default (every other site)' : id}
                </span>
                <input
                  id={`spend-cap-daily-${id}`}
                  aria-label={`Daily cap for ${id === '*' ? 'the fleet default' : id}`}
                  inputMode="decimal"
                  className="h-8 rounded border border-gray-300 px-2 text-sm tabular-nums"
                  placeholder="No cap"
                  value={d.daily}
                  onChange={(e) => set({ daily: e.target.value })}
                />
                <input
                  id={`spend-cap-monthly-${id}`}
                  aria-label={`Monthly cap for ${id === '*' ? 'the fleet default' : id}`}
                  inputMode="decimal"
                  className="h-8 rounded border border-gray-300 px-2 text-sm tabular-nums"
                  placeholder="No cap"
                  value={d.monthly}
                  onChange={(e) => set({ monthly: e.target.value })}
                />
                <input
                  id={`spend-cap-note-${id}`}
                  aria-label={`Note for ${id === '*' ? 'the fleet default' : id}`}
                  className="col-span-2 sm:col-span-1 h-8 rounded border border-gray-300 px-2 text-sm"
                  placeholder="Why this cap"
                  value={d.note}
                  onChange={(e) => set({ note: e.target.value })}
                />
                <div className="col-span-2 sm:col-span-1 flex items-center gap-1 justify-end">
                  <Button variant="secondary" size="sm" type="button" onClick={() => save(id)} disabled={saving === id}>
                    {saving === id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span className="ml-1">Save</span>
                  </Button>
                  {id !== '*' && (
                    <button
                      type="button"
                      onClick={() => remove(id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                      title="Use the fleet default"
                      aria-label={`Remove ${id}'s own caps`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <div className="flex items-center gap-2 pt-2">
            <select
              id="spend-cap-new-site"
              aria-label="Site to give its own caps"
              className="h-8 rounded border border-gray-300 px-2 text-sm"
              value={newSite}
              onChange={(e) => setNewSite(e.target.value)}
            >
              <option value="">Give a site its own caps…</option>
              {sites
                .filter((s) => !capped.has(s.site_id))
                .map((s) => (
                  <option key={s.site_id} value={s.site_id}>
                    {s.workspace_name ? `${s.workspace_name} (${s.site_id})` : s.site_id}
                  </option>
                ))}
            </select>
            <Button variant="secondary" size="sm" type="button" onClick={addSite} disabled={!newSite}>
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
